/* eslint-disable no-prototype-builtins */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2020 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import Notify from "@eisinc/App/Components/Notify";
import QtyField from "@eisinc/App/Components/Qty/QtyField";
import MiniCartActionButtons from "@eisinc/App/Components/XngMiniCart/XngMiniCartActions";
import { minicartStyles } from "@eisinc/App/Components/XngMiniCart/XngMiniCartStyles";
import { getValidatedQty } from "@eisinc/App/Utilities/helpers";
import Button, { ButtonIcon } from "@eisinc/MobiusOverrides/Button";
import LazyImage from "@eisinc/MobiusOverrides/LazyImage";
import { UnitOfMeasureData } from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import { QTY_MAX_VALUE } from "@eisinc/XDL/constants";
import Delete from "@eisinc/XDL/Icons/Delete";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasQuoteRequiredProducts,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import parse from "html-react-parser";
import React from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";

interface MiniCartHeadingProps {
    count: number;
    subTotal?: string;
    hasQuoteRequiredProducts?: boolean;
    lessThanOrder: boolean;
    cartNotPriced: boolean;
    canModifyCuts: boolean;
    cutsDoesNotEqualQty: boolean;
    isCheckoutDisabled: boolean;
}

interface MiniCartLinesProps {
    setShowSpinner: any;
    cartLine: CartLineModel;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: (id: string) => void;
    cartType?: string;
}

interface MiniCartLineImageProps {
    smallImagePath?: string;
    productUri?: string;
    altText?: string;
}

interface MiniCartLineDesciption {
    productUri?: string;
    shortDescription?: string;
}

interface MiniCartLineNumbersProps {
    manufacturerItem?: string;
    customerName?: string;
    specNumber?: string;
    erpNumber?: string;
}

interface MiniCartLineLeftSideProps {
    setShowSpinner: any;
    cartLine: CartLineModel;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    cartType?: string;
}

const MiniCartHeading = ({
    count,
    subTotal,
    hasQuoteRequiredProducts,
    lessThanOrder,
    cartNotPriced,
    canModifyCuts,
    cutsDoesNotEqualQty,
    isCheckoutDisabled,
}: MiniCartHeadingProps) => {
    return (
        <StyledWrapper {...minicartStyles.miniCartHeadingStyles}>
            <Typography as="h3">{translate("Cart Summary")}</Typography>
            <StyledWrapper {...minicartStyles.miniCartHeadingTotalStyles}>
                <StyledWrapper>
                    <Typography as="strong" className="label">
                        {translate("Items")}
                    </Typography>
                    <Typography as="span">{count || 0}</Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <Typography as="strong" className="label">
                        {translate("SubTotal")}
                    </Typography>
                    <Typography as="span">{subTotal || 0}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            {(hasQuoteRequiredProducts ||
                lessThanOrder ||
                cartNotPriced ||
                canModifyCuts ||
                cutsDoesNotEqualQty ||
                isCheckoutDisabled) && (
                <StyledWrapper {...minicartStyles.msgWrapper}>
                    {hasQuoteRequiredProducts && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            showCloseButton={true}
                            body={siteMessage("Custom_Cart_has_quote_required_products")}
                        />
                    )}
                    {lessThanOrder && count > 0 && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            showCloseButton={true}
                            body={siteMessage("Cart_LessThanOrderThreshold")}
                        />
                    )}
                    {cartNotPriced && count > 0 && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            showCloseButton={true}
                            body={siteMessage("Cart_PriceNotAvailable")}
                        />
                    )}
                    {canModifyCuts && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="info"
                            showCloseButton={true}
                            body={siteMessage("Custom_Cart_CanModifyCuts")}
                        />
                    )}
                    {cutsDoesNotEqualQty && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            showCloseButton={true}
                            body={siteMessage("Custom_Cart_CutLengthsDoesNotMeetQtyOrdered")}
                        />
                    )}
                    {isCheckoutDisabled && (
                        <Notify
                            {...minicartStyles.notificationContainers}
                            messageType="warning"
                            showCloseButton={true}
                            body={siteMessage("Custom_Cart_Quickfab_Checkout_Fulfillment_Method")}
                        />
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const MiniCartLinesHeading = () => {
    return (
        <StyledWrapper {...minicartStyles.cartLinesHeadingStyles}>
            <StyledWrapper className="description">
                <Typography as="span">{translate("Item Description")}</Typography>
            </StyledWrapper>
            <StyledWrapper className="qty">
                <Typography as="span">{translate("qty")}</Typography>
            </StyledWrapper>
            <StyledWrapper className="price">
                <Typography as="span">{translate("Price/Unit")}</Typography>
                <Typography as="span" className="total">
                    {translate("Extended")}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const MiniCartLineImage = ({ smallImagePath, productUri, altText }: MiniCartLineImageProps) => {
    return (
        <Clickable href={productUri} {...minicartStyles.cartLineImageStyles}>
            <LazyImage src={smallImagePath} altText={altText}></LazyImage>
        </Clickable>
    );
};

const MiniCartLineDescription = ({ productUri, shortDescription }: MiniCartLineDesciption) => {
    // using react-lines will slow the overlay from rendering since we call for the lib on every hover
    // for now 3 lines only showing without ellipsis
    const description = shortDescription || "";
    return (
        <Clickable {...minicartStyles.cartLineDescriptionStyles} href={productUri}>
            {parse(description)}
        </Clickable>
    );
};

const MiniCartLineNumbers = ({ specNumber, erpNumber, manufacturerItem, customerName }: MiniCartLineNumbersProps) => {
    // to show only the first two non-empty values
    const mapShow = {
        showMFR: !customerName || (customerName && !specNumber) || (!customerName && !specNumber),
        showERP:
            (!customerName && !specNumber) ||
            (!customerName && !manufacturerItem) ||
            (!specNumber && !manufacturerItem) ||
            (!customerName && !manufacturerItem && !specNumber),
    };
    return (
        <StyledWrapper {...minicartStyles.cartLineNumbersStyles}>
            {customerName && (
                <StyledWrapper>
                    <Typography as="strong">{siteMessage("Custom_Your_Part_#", "Your Part #:")}</Typography>
                    <Typography as="span">{customerName}</Typography>
                </StyledWrapper>
            )}
            {specNumber && (
                <StyledWrapper>
                    <Typography as="strong">{siteMessage("Custom_Spec_#", "Spec #:")}</Typography>
                    <Typography as="span">{specNumber}</Typography>
                </StyledWrapper>
            )}
            {manufacturerItem && mapShow.showMFR && (
                <StyledWrapper>
                    <Typography as="strong">{siteMessage("Custom_MFR_#", "MFR #:")}</Typography>
                    <Typography as="span">{manufacturerItem}</Typography>
                </StyledWrapper>
            )}
            {erpNumber && mapShow.showERP && (
                <StyledWrapper>
                    <Typography as="strong">{siteMessage("Custom_Part_#", "EIS Part #:")}</Typography>
                    <Typography as="span">{erpNumber}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const MiniCartLineLeftSide: React.FC<MiniCartLineLeftSideProps> = ({
    cartLine,
    updateCartLine,
    cartType,
    setShowSpinner,
}) => {
    // using .toLocaleString() will cause out-of-range issues for big vlaues
    const [stateValue, setValue] = React.useState(cartLine.qtyOrdered?.toString());
    const [allowFractionalQty, setAllowFractionalQty] = React.useState(false);
    const [minQty, setMinQty] = React.useState(1);
    const [multipleSaleQty, setMultipleSaleQty] = React.useState(1);
    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(cartLine?.properties?.unitOfMeasureData || "[]");
    const unitOfMeasure = cartLine?.unitOfMeasureDescription || cartLine?.unitOfMeasureDisplay;

    const regexp = allowFractionalQty ? /^[0-9]*[.,]?[0-9]*$/ : /^[0-9\b]+$/;

    const isValidNumberic = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            event.stopPropagation();
            if (stateValue && isValidNumberic(stateValue) && parseFloat(stateValue) <= QTY_MAX_VALUE) {
                (event.target as any).blur();
            }
        }
    };

    React.useEffect(() => {
        setValue(cartLine.qtyOrdered?.toString());
    }, [cartLine.qtyOrdered]);

    React.useEffect(() => {
        if (!cartLine) {
            return;
        }
        setValue(cartLine.qtyOrdered?.toString());

        let selectedUOM;
        if (unitOfMeasuresData && unitOfMeasure) {
            selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
        }

        if (selectedUOM && selectedUOM.length) {
            const minQtyVal = parseFloat(selectedUOM[0].MinimumOrderQuantity) || 1;
            const msqVal = parseFloat(selectedUOM[0].MultipleSaleQuantity) || 1;
            const isFractionalMoq = !Number.isInteger(minQtyVal);
            setAllowFractionalQty(isFractionalMoq);
            setMinQty(minQtyVal);
            setMultipleSaleQty(msqVal);
        } else {
            if (cartLine.properties?.isFractionalMoq) {
                const isFractionalMoq = cartLine.properties?.isFractionalMoq?.toLowerCase() === "true";
                setAllowFractionalQty(isFractionalMoq);
            }
            const minQtyVal = parseFloat(cartLine.properties?.minimumOrderQty) || 1;
            const msqVal = parseFloat(cartLine.properties?.multipleSaleQty) || 1;
            setMinQty(minQtyVal);
            setMultipleSaleQty(msqVal);
        }
    }, [cartLine]);

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isValidNumberic(event.currentTarget.value)) {
            event.preventDefault();
            return;
        }
        const value = event.currentTarget.value;
        setValue(value);
    };

    const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const stringValue = event ? event.currentTarget.value : stateValue;
        const value = Number.parseFloat(stringValue || "0");

        if (Number.isNaN(value) || value <= 0 || value > QTY_MAX_VALUE) {
            setValue(cartLine.qtyOrdered?.toString());
            return;
        }

        const selectedUOM = unitOfMeasuresData?.find(uom => uom.UnitOfMeasure === unitOfMeasure);
        const minQty = selectedUOM
            ? parseFloat(selectedUOM.MinimumOrderQuantity) || 1
            : parseFloat(cartLine.properties.minimumOrderQty) || 1;
        const msq = selectedUOM
            ? parseFloat(selectedUOM.MultipleSaleQuantity) || 1
            : parseFloat(cartLine.properties.multipleSaleQty) || 1;

        const newValue = getValidatedQty(value, minQty, msq);

        setValue(newValue.toString());

        if (newValue !== cartLine.qtyOrdered) {
            setShowSpinner(true);
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered: newValue,
                    properties: { ...cartLine.properties, oldQty: cartLine.qtyOrdered?.toString() || "1" },
                },
                onSuccess: () => {
                    setShowSpinner(false);
                },
            });
        }
    };

    const pricingUnit = cartLine?.properties?.realTimePricingUom || "";

    return (
        <StyledWrapper {...minicartStyles.cartLineLeftSideStylesWrapper}>
            <StyledWrapper {...minicartStyles.cartLineOrderedQtyWrapper} className="qty">
                <QtyField
                    value={stateValue}
                    // xng-change - EIS-4192 - adding aria-label for accessibility
                    aria-label={translate("QTY")}
                    showLabel={false}
                    min={0}
                    maxLength={9}
                    onKeyPress={keyPressHandler}
                    onChange={qtyChangeHandler}
                    onBlur={onBlurHandler}
                    data-test-selector="mini_cart_qtyOrdered"
                    disabled={
                        cartLine.isPromotionItem || cartType === "Job" || !cartLine.isActive || cartLine.isRestricted
                    }
                    allowFractionalQty={allowFractionalQty}
                    multipleSaleQty={multipleSaleQty}
                    minimumOrderQty={minQty}
                />
            </StyledWrapper>
            <StyledWrapper {...minicartStyles.cartLinePriceTotalWrapper}>
                {!cartLine.quoteRequired && cartLine.pricing?.unitNetPrice !== 0 && (
                    <Typography as="span" className="price">
                        {cartLine?.pricing?.unitNetPriceDisplay}{" "}
                        {unitOfMeasure ? `/ ${pricingUnit} ${unitOfMeasure}` : ""}
                    </Typography>
                )}
                {!cartLine.quoteRequired && cartLine.pricing?.unitNetPrice !== 0 && (
                    <Typography as="span" className="total">
                        {cartLine?.pricing?.extendedUnitNetPriceDisplay}
                    </Typography>
                )}

                {((cartLine.pricing?.unitNetPrice === 0 && !cartLine.isPromotionItem) || cartLine.quoteRequired) && (
                    <Typography as="span" className="quote">
                        {translate("Requires Quote")}
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const MiniCartLine: React.FC<MiniCartLinesProps> = ({
    cartLine,
    removeCartLine,
    updateCartLine,
    cartType,
    setShowSpinner,
}) => {
    const isInactive = !cartLine.isActive;
    const isRestricted = cartLine.isRestricted;

    return (
        <StyledWrapper {...minicartStyles.cartLineStyles}>
            <Button
                className="remove"
                onClick={() => removeCartLine(cartLine.id)}
                {...minicartStyles.cartLineRemoveButtonStyles}
            >
                <ButtonIcon src={Delete} size={14} />
            </Button>
            <StyledWrapper {...minicartStyles.cartLineRightSideStyles}>
                <MiniCartLineImage
                    smallImagePath={cartLine.smallImagePath}
                    altText={cartLine.altText}
                    productUri={cartLine.productUri}
                />
                <StyledWrapper {...minicartStyles.cartLineDescriptionNumbersWrapper}>
                    <MiniCartLineDescription
                        shortDescription={cartLine.shortDescription}
                        productUri={cartLine.productUri}
                    />
                    <MiniCartLineNumbers
                        manufacturerItem={cartLine.manufacturerItem}
                        customerName={cartLine.customerName}
                        specNumber={cartLine.properties["specNumber"]}
                        erpNumber={cartLine.erpNumber}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...minicartStyles.cartLineLeftSideStyles}>
                <MiniCartLineLeftSide
                    setShowSpinner={setShowSpinner}
                    cartLine={cartLine}
                    updateCartLine={updateCartLine}
                    cartType={cartType}
                />
                {isInactive && (
                    <Typography as="span" color="danger.main" className="error-message">
                        {siteMessage("Custom_Mini_Cart_InactiveProduct_Msg")}
                    </Typography>
                )}
                {isRestricted && (
                    <Typography as="span" color="danger.main" className="error-message">
                        {siteMessage("Custom_Mini_Cart_RestrictedProduct_Msg")}
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

/**
 *  Mini-cart component Main entrance
 */

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        isLoading: getCurrentCartState(state).isLoading,
        isEmpty: isCartEmpty(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),

        isRemovingCartLine: state.pages.cart?.hasOwnProperty("isRemovingCartLine")
            ? state.pages.cart?.isRemovingCartLine
            : null,
    };
};

const mapDispatchToProps = {
    updateCartLine,
    removeCartLine,
};

type OwnProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const XngMiniCart: React.FC<OwnProps> = ({
    cart,
    updateCartLine,
    removeCartLine,
    isEmpty,
    isRemovingCartLine,
    hasQuoteRequiredProducts,
    isLoading,
}) => {
    const [showSpinner, setShowSpinner] = React.useState(false);
    const count = cart?.totalCountDisplay;
    const subTotal = cart?.orderSubTotalDisplay;
    const [belowOrderThreshold, setOrderThreshold] = React.useState(false);
    const [canModifyCuts, setCanModifyCuts] = React.useState(false);
    const [removedCartId, setRemovedCartId] = React.useState("");
    const [cutsDoesNotEqualQty, setCutsDoesNotEqualQty] = React.useState(false);
    const extendedCutTotal: boolean[] = [];
    // Xng-change: EIS-3697: adding a check when checkout is disabled for quickFab products
    const isCheckoutDisabled = cart?.properties.disablePickupMethod === "true" && cart?.fulfillmentMethod === "PickUp";

    const handleRemoveLineWithSpinner = (cartLineId: string) => {
        setRemovedCartId(cartLineId);
        removeCartLine({ cartLineId });
    };

    React.useEffect(() => {
        const orderMin = cart?.properties["orderLessThanMinimumOrderValue"] === "true";
        const canModifyCut = cart?.cartLines?.some(
            line =>
                line.properties["allowCutWire"] === "true" &&
                (line.properties["cutWire"] === undefined || line.properties["cutWire"] === ""),
        );
        setCanModifyCuts(canModifyCut || false);
        setOrderThreshold(orderMin);
        // eslint-disable-next-line array-callback-return
        cart?.cartLines?.map(line => {
            if (
                line.properties["allowCutWire"] === "true" &&
                line.properties?.hasOwnProperty("cutWire") &&
                line.properties["cutWire"] !== ""
            ) {
                const cutObj = JSON.parse(line.properties["cutWire"]);
                const newExtendedTotal = cutObj.reduce(function (prev: any, current: any) {
                    return prev + parseInt(current.total, 10);
                }, 0);
                extendedCutTotal.push(line.qtyOrdered !== newExtendedTotal);
            }
        });
        setCutsDoesNotEqualQty(extendedCutTotal.some(o => o));
    }, [cart]);

    React.useEffect(() => {
        if (isRemovingCartLine && isRemovingCartLine?.hasOwnProperty(removedCartId)) {
            setShowSpinner(true);
        } else {
            setShowSpinner(false);
        }
    }, [isRemovingCartLine]);

    return (
        <StyledWrapper {...minicartStyles.miniCartContainerStyles}>
            <MiniCartHeading
                count={count || 0}
                subTotal={subTotal}
                lessThanOrder={belowOrderThreshold}
                canModifyCuts={canModifyCuts}
                hasQuoteRequiredProducts={hasQuoteRequiredProducts}
                cartNotPriced={cart?.cartNotPriced || false}
                cutsDoesNotEqualQty={cutsDoesNotEqualQty}
                isCheckoutDisabled={isCheckoutDisabled}
            />
            <StyledWrapper {...minicartStyles.fancyOverflowStyles} className={isEmpty ? "empty-cart" : ""}>
                {!isEmpty && <MiniCartLinesHeading />}
                {(showSpinner || isLoading) && (
                    <StyledWrapper {...minicartStyles.spinnerWrapper}>
                        <LoadingSpinner color="primary.main"></LoadingSpinner>
                    </StyledWrapper>
                )}
                {cart && cart?.cartLines?.length ? (
                    cart.cartLines.map((cartLine: CartLineModel) => {
                        return (
                            <MiniCartLine
                                setShowSpinner={setShowSpinner}
                                cartType={cart.type}
                                cartLine={cartLine}
                                key={cartLine.id}
                                removeCartLine={handleRemoveLineWithSpinner}
                                updateCartLine={updateCartLine}
                            />
                        );
                    })
                ) : (
                    <Typography as="p">{siteMessage("Custom_Empty_Mini_Cart_Msg")}</Typography>
                )}
            </StyledWrapper>
            <MiniCartActionButtons cutsDoesNotEqualQty={cutsDoesNotEqualQty} />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(XngMiniCart);
